import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { ThemeProvider } from '@material-ui/core/styles';
import { selectMainTheme } from 'app/store/fuse/settingsSlice';
import { Dialog, DialogContent, DialogTitle, Tooltip as MuiTooltip } from '@material-ui/core';
import { format } from 'date-fns';
import { hu } from 'date-fns/locale';

import ConfigurableDataTable from 'app/admin/common/component/ConfigurableDataTable';
import DateRangeToolbar from 'app/admin/common/component/DateRangeToolbar';
import { Button } from './Pipeline';

import { useOptimizationPipeline } from '../OptimizationPipelineProvider';

export default function UnitHistoryPopup() {
    const [mergerData, setMergerData] = useState([]);
    const [mergerKeys, setMergerKeys] = useState([]);

    const [minDate, setMinDate] = useState(null);
    const [maxDate, setMaxDate] = useState(null);
    const [dateFrom, setDateFrom] = useState(null);
    const [dateTo, setDateTo] = useState(null);

    const [visibleDynamicKeys, setVisibleDynamicKeys] = useState([]);

    const [syntheticFilter, setSyntheticFilter] = useState('all');

    const { setPopup, optimizationPipelineData, mergedMeasurementDataList, mergedMeasurementData } = useOptimizationPipeline();
    const mainTheme = useSelector(selectMainTheme);

    function collectMergerData(data) {
        return data
            .map(entry => {
                if (!entry?.meta) return null;

                const meta = entry.meta;

                // 1) először próbáljuk a sima "merger" kulcsot
                let merger = meta.merger;

                // 2) ha nincs, akkor keressük a "merger:..." formátumú kulcsot
                if (!merger) {
                    const mergerKey = Object.keys(meta).find(k => k.startsWith('merger:'));
                    if (!mergerKey) return null;
                    merger = meta[mergerKey];
                }

                if (!merger || !merger.inputs) return null;

                const inputs = merger.inputs;

                let syntheticFlag = false;
                for (const v of Object.values(inputs)) {
                    if (v?.synthetic_data === true) {
                        syntheticFlag = true;
                        break;
                    }
                }

                return { ts: entry.ts, data: inputs, syntheticFlag };
            })
            .filter(Boolean);
    }

    function getAllMergerKeys(data) {
        const set = new Set();
        for (const entry of data) {
            if (entry && entry.data) {
                Object.keys(entry.data).forEach(k => set.add(k));
            }
        }
        return Array.from(set);
    }

    function cellString(row, key) {
        const v = row?.data?.[key];

        if (!v) {
            return (
                <div className='flex flex-col text-12 px-5 py-3 rounded bg-red-100 text-red-900'>
                    <span className='font-semibold'>Missing data</span>
                </div>
            );
        }

        const synth = v?.synthetic_data;

        if (synth === true) {
            return (
                <div className='flex flex-col text-12 px-5 py-3 rounded bg-amber-100 text-amber-900'>
                    <span className='font-semibold'>Synthetic</span>
                </div>
            );
        }

        if (synth === false) {
            return (
                <div className='flex text-12 px-5 py-3 rounded bg-green-100 text-green-900 whitespace-nowrap'>
                    <span className='font-semibold'>Measured</span>
                </div>
            );
        }

        return (
            <div className='flex text-12 px-5 py-3 rounded bg-gray-100 text-gray-900 whitespace-nowrap'>
                <span className='font-semibold'>Unknown</span>
            </div>
        );
    }

    useEffect(() => {
        if (mergedMeasurementDataList)
            setMergerData(collectMergerData(mergedMeasurementDataList));
    }, [mergedMeasurementDataList]);

    useEffect(() => {
        if (mergerData)
            setMergerKeys(getAllMergerKeys(mergerData));
    }, [mergerData]);

    useEffect(() => {
        setVisibleDynamicKeys(mergerKeys);
    }, [mergerKeys]);

    useEffect(() => {
        if (mergerData?.length > 0) {
            const first = new Date(mergerData[0].ts);
            const last = new Date(mergerData[mergerData.length - 1].ts);
            setMinDate(first);
            setMaxDate(last);
            setDateFrom(first);
            setDateTo(last);
        } else {
            setMinDate(null);
            setMaxDate(null);
            setDateFrom(null);
            setDateTo(null);
        }
    }, [mergerData]);

    const processorInfoByKey = useMemo(() => {
        const map = {};
        for (const row of mergerData) {
            const data = row?.data || {};
            for (const [key, v] of Object.entries(data)) {
                if (map[key]) continue;
                const proc = v?.data_origin?.data_processor;
                const name = v?.data_origin?.data_processor_name;
                if (proc || name) map[key] = { proc: proc || '-', name: name || '-' };
            }
        }
        return map;
    }, [mergerData]);

    const columns = useMemo(() => {
        const base = [
            {
                key: 'ts',
                header: 'Timestamp',
                sortable: true,
                format: v => (v ? format(new Date(v), 'yyyy.MM.dd', { locale: hu }) : '-'),
            },
            {
                key: 'syntheticFlag',
                header: 'Synthetic',
                accessor: row => {
                    if (row.syntheticFlag === true) { return <div className='px-4 py-2 rounded bg-amber-100 text-amber-900 font-semibold'>Yes</div>; }
                    if (row.syntheticFlag === false) { return <div className='px-4 py-2 rounded bg-green-100 text-green-900 font-semibold'>No</div>; }
                    return <div className='px-4 py-2 rounded bg-red-100 text-red-900 font-semibold'>Missing</div>;
                },
            }
        ];

        const dynamic = mergerKeys
            .filter(k => visibleDynamicKeys.includes(k))
            .map(k => {
                const info = processorInfoByKey[k] || { proc: '-', name: '-' };
                return (
                    {
                        key: k,
                        header: (
                            <div className='flex flex-col leading-tight'>
                                <span className='font-semibold'>{k}</span>
                                <span className='text-10 opacity-70'>{info.proc}</span>
                                <span className='text-10 opacity-70'>{info.name}</span>
                            </div>
                        ),
                        accessor: row => cellString(row, k),
                    }
                );
            });

        return [...base, ...dynamic];
    }, [mergerKeys, processorInfoByKey, visibleDynamicKeys]);

    const filteredRows = useMemo(() => {
        if (!mergerData?.length) return [];

        let rows = mergerData;

        if (dateFrom && dateTo) {
            const from = new Date(dateFrom).getTime();
            const to = new Date(dateTo).getTime();
            rows = rows.filter(r => {
                const t = new Date(r.ts).getTime();
                return t >= from && t <= to;
            });
        }

        if (syntheticFilter === 'yes') {
            rows = rows.filter(r => r.syntheticFlag === true);
        } else if (syntheticFilter === 'no') {
            rows = rows.filter(r => r.syntheticFlag === false);
        }

        return rows;
    }, [mergerData, dateFrom, dateTo, syntheticFilter]);

    const ToolbarChip = ({ label, title, onClick, disabled = false, active = false }) => (
        <MuiTooltip title={title || label} arrow>
            <span>
                <button
                    type='button'
                    onClick={onClick}
                    disabled={disabled}
                    aria-pressed={active}
                    className={
                        'text-10 leading-none font-medium px-6 py-4 rounded border transition-colors ' +
                        (active
                            ? 'bg-gray-200 text-black border-transparent shadow'
                            : 'bg-transparent text-black/70 border-gray-100 hover:bg-gray-50') +
                        (disabled ? ' opacity-40 cursor-not-allowed' : ' focus:outline-none focus:ring-2 focus:ring-blue-300')
                    }
                >
                    {label}
                </button>
            </span>
        </MuiTooltip>
    );

    const SyntheticToolbar = () => (
        <div className='flex items-center gap-8'>
            <div className='text-12 opacity-70 mr-4'>Synthetic:</div>
            <div className='flex items-center gap-2'>
                <ToolbarChip label='All' title='All rows' active={syntheticFilter === 'all'} onClick={() => setSyntheticFilter('all')} />
                <ToolbarChip label='Yes' title='Only synthetic' active={syntheticFilter === 'yes'} onClick={() => setSyntheticFilter('yes')} />
                <ToolbarChip label='No' title='Only non-synthetic' active={syntheticFilter === 'no'} onClick={() => setSyntheticFilter('no')} />
            </div>
        </div>
    );

    const ColumnToggle = () => (
        <div className='flex items-center gap-8 overflow-x-auto whitespace-nowrap pr-4'>
            <div className='text-12 opacity-70 mr-4 flex-shrink-0'>Columns:</div>
            {mergerKeys.map(k => {
                const active = visibleDynamicKeys.includes(k);
                return (
                    <ToolbarChip
                        key={k}
                        label={k}
                        title={active ? 'Hide column' : 'Show column'}
                        active={active}
                        onClick={() => { setVisibleDynamicKeys(prev => active ? prev.filter(x => x !== k) : [...prev, k]); }}
                    />
                );
            })}
        </div>
    );

    return (
        <ThemeProvider theme={mainTheme}>

            <Dialog open fullWidth maxWidth={mergerData.length ? 'lg' : 'sm'}>

                <DialogTitle disableTypography className='border-b flex flex-col p-0'>

                    <div className='flex items-center justify-between gap-32 px-32 py-16'>
                        <div className='flex gap-8'>
                            Unit | History - {mergedMeasurementData.name}
                        </div>
                        <Button label='Close' onClick={() => setPopup(null)} />
                    </div>

                    {mergerData.length > 0 && (<>
                        <DateRangeToolbar
                            from={dateFrom}
                            to={dateTo}
                            minDate={minDate}
                            maxDate={maxDate}
                            onChange={({ from, to }) => {
                                setDateFrom(from || null);
                                setDateTo(to || null);
                            }}
                        />
                        <div className='flex justify-between gap-16 px-32 pb-8'>
                            <SyntheticToolbar />
                            <ColumnToggle />
                        </div>
                    </>)}

                </DialogTitle>

                <DialogContent className='w-full flex flex-col p-20 overflow-hidden' style={{ height: mergerData.length ? '520px' : 'auto' }}>
                    <ConfigurableDataTable rows={filteredRows} columns={columns} searchable={false} />
                </DialogContent>

            </Dialog>

        </ThemeProvider>
    );
}
